import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

//============================================================================================================
// Funtion to auto-fit columns in an Excel worksheet
//============================================================================================================
function autoFitColumns(worksheet) {
  worksheet.columns?.forEach((column) => { // Iterate through each column
    let max = 10; // Start with a minimum width
    column.eachCell?.({ includeEmpty: true }, (cell) => { // Iterate through each cell in the column
      const v = cell.value == null ? "" : String(cell.value); // Convert cell value to string
      max = Math.max(max, v.length + 2); // Update max width considering padding
    });
    column.width = max; // Set the column width
  });
}

//============================================================================================================
// Function to export a flat Excel file
//============================================================================================================
export async function exportFlatExcel({ sheetName, filename, headers, rows }) {
  const wb = new ExcelJS.Workbook(); // Create a new workbook
  const ws = wb.addWorksheet(sheetName); // Add a new worksheet with the specified name

  ws.addRow(headers); // Add the headers as the first row
  rows.forEach((r) => ws.addRow(r)); // Add each row of data

  autoFitColumns(ws); // Auto-fit the columns for better readability

  const buffer = await wb.xlsx.writeBuffer(); // Write the workbook to a buffer
  saveAs(new Blob([buffer]), filename); // Save the buffer as a file with the specified filename
}

//============================================================================================================
// Function to export a grouped Excel file
//============================================================================================================
export async function exportGroupedExcel({
  sheetName,
  filename,
  singles, // Singles are columns with a single value (e.g., Lane, destination)
  groups,  // groups are columns with multiple values (e.g., Bid, Best Bid, Carrier, etc)
  data,    // data is an array of objects where each object represents a row
  buildRow // buildRow is a function that takes an item and returns an array representing the row data
}) {
  const wb = new ExcelJS.Workbook(); // Create a new workbook
  const ws = wb.addWorksheet(sheetName); // Add a new worksheet with the specified name

  // Prepare header rows
  const headerRow1 = [];
  const headerRow2 = [];

  // Add singles and groups to the header rows
  singles.forEach(s => {
    headerRow1.push(s.label);
    headerRow2.push(""); // placeholder 
  });

  // Add groups to the header rows
  groups.forEach(g => {
    // line 1: add group label
    headerRow1.push(g.label);
    // fill line 2 with children
    g.children.forEach(ch => headerRow2.push(ch));
    // fill the rest of line 2 with empty strings
    for (let i = 1; i < g.children.length; i++) headerRow1.push("");
  });

  // Add the header rows to the worksheet
  const r1 = ws.addRow(headerRow1);
  const r2 = ws.addRow(headerRow2);

  // Merge cells:
  // - singles: merge vertically (row1..row2) each column of singles
  let colIndex = 1;
  singles.forEach(() => {
    ws.mergeCells(1, colIndex, 2, colIndex); // row1..row2 no mesmo col
    colIndex += 1;
  });

  // - groups: merge horizontally (in row 1) the group label in the columns of the children
  groups.forEach(g => {
    const span = g.children.length;
    if (span > 1) {
      ws.mergeCells(1, colIndex, 1, colIndex + span - 1);
    }
    colIndex += span;
  });

  // Add data rows
  data.forEach(item => {
    const row = buildRow(item);
    ws.addRow(row);
  });

  autoFitColumns(ws);

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), filename);
}
